import React, { useContext, useState } from "react";
import { Button } from "components/button";
import { VendorContext } from "contexts/VendorContext";
import styles from "./VendorsToolBar.module.scss";
import globalVendorsStyles from "../Vendors.module.css";
import { useTranslation } from "react-i18next";
import Tooltip from "@material-ui/core/Tooltip";
import VendorFormModal from "../components/VendorFormModal";
import { useHeritageConnection } from "../VendorBody/VendorItems/VendorConnectionModal/hooks/useHeritageConnection";
import { useHeritageAuthStatus } from "hooks/useHeritageAuthStatus";
import { getHeritageVendorStatus, heritageVendorStatusBadgeClass, isHeritageCompanyConnected } from "../utils/heritageConnectionStatus";
import { useCompanyFeature } from "hooks/useCompanyFeature";
import {
  heritageCatalogSyncShortLabel,
  resolveHeritageBranchDetails,
} from "../components/heritageCatalogSyncMessage";
import { useHeritageCatalogDownloadStatus } from "../hooks/useHeritageCatalogDownloadStatus";
import { useQuery } from "react-query";
import axios from "axios";

const VendorsToolBar = ({ refetch }: { refetch?: () => void | Promise<unknown> }) => {
  const { t } = useTranslation();
  const tBase = "views.vendors.VendorsToolBar.VendorsToolBar";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { currentVendor, heritageConnectPhase, openVendorEditModal } = useContext(VendorContext);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // Feature off => Heritage-tagged vendors present as ordinary vendors: no
  // badge, no status, no download labels.
  const heritageFeatureEnabled = useCompanyFeature("heritage");
  const isHeritageVendor = heritageFeatureEnabled && Boolean(currentVendor?.is_heritage);
  const { data: heritageConnection, isLoading: isHeritageConnectionLoading } = useHeritageConnection();
  const { data: heritageAuthStatus, isLoading: isHeritageAuthLoading } = useHeritageAuthStatus({
    enabled: isHeritageVendor,
  });
  const isHeritageConnected =
    isHeritageCompanyConnected(heritageConnection) || heritageAuthStatus?.connected === true;
  const hasHeritageCredentials = Boolean(heritageConnection?.credentials_exist);
  const heritageStatus =
    isHeritageVendor && !isHeritageConnectionLoading && !isHeritageAuthLoading
      ? getHeritageVendorStatus(
          isHeritageConnected,
          currentVendor!.is_heritage_verified === true,
          {
            connectPhase: heritageConnectPhase,
            authStatus: heritageAuthStatus
              ? {
                  connected: heritageAuthStatus.connected,
                  hasCredentials: heritageAuthStatus.hasCredentials,
                  tokenExpired:
                    heritageAuthStatus.tokenExpired ??
                    heritageAuthStatus.token?.isExpired ??
                    false,
                  tokenMissing: heritageAuthStatus.tokenMissing ?? false,
                  connectionState: heritageAuthStatus.connectionState,
                }
              : undefined,
          }
        )
      : null;

  const heritageBadgeClass = heritageStatus
    ? heritageVendorStatusBadgeClass(heritageStatus.tone)
    : "";
  const heritageBadgeLabel = heritageStatus ? tr(heritageStatus.labelKey) : "";
  const heritageBadgeOpensCredentials =
    isHeritageVendor &&
    heritageStatus?.labelKey === "Heritage Credentials Missing";
  const heritageBadgeOpensConnect =
    isHeritageVendor &&
    (heritageStatus?.labelKey === "Heritage Token Expired" ||
      heritageStatus?.labelKey === "Heritage Reconnect Required");
  const heritageBadgeTooltip = heritageStatus?.tooltipKey
    ? tr(
        heritageBadgeOpensCredentials
          ? "Click to open Edit Vendor and add Heritage credentials."
          : heritageBadgeOpensConnect
            ? "Click to open Edit Vendor and reconnect Heritage."
            : heritageStatus.tooltipKey
      )
    : undefined;

  const handleHeritageBadgeClick = () => {
    openVendorEditModal({ focusHeritageCredentials: true });
  };

  const catalogDownloadStatusQuery = useHeritageCatalogDownloadStatus({
    enabled: isHeritageVendor && hasHeritageCredentials,
    refetchInterval: isHeritageVendor && hasHeritageCredentials ? 4000 : false,
  });

  const vendorHeritageSettingsQuery = useQuery<{
    branchId: number;
    branch_name: string | null;
    branch_code: string | null;
  }>(
    ["heritage-vendor-settings", currentVendor?.vendor_id],
    () =>
      axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/vendors/${currentVendor!.vendor_id}/heritage/settings`
        )
        .then((res) => res.data),
    {
      enabled: isHeritageVendor && hasHeritageCredentials && Boolean(currentVendor?.vendor_id),
      staleTime: 1000 * 30,
    }
  );

  const catalogSyncBranchDetails = resolveHeritageBranchDetails(
    vendorHeritageSettingsQuery.data?.branchId,
    vendorHeritageSettingsQuery.data,
    heritageConnection?.branches
  );

  const catalogLastSyncedLabel = (() => {
    if (!isHeritageVendor || !hasHeritageCredentials) return null;
    if (catalogDownloadStatusQuery.data?.inProgress) {
      return heritageCatalogSyncShortLabel({
        vendorName: currentVendor?.vendor_name,
        branchName: catalogSyncBranchDetails.branchName,
        branchCode: catalogSyncBranchDetails.branchCode,
        syncKind: catalogDownloadStatusQuery.data?.syncKind ?? "branch",
      });
    }
    const lastSyncedAt = catalogDownloadStatusQuery.data?.lastSyncedAt;
    if (!lastSyncedAt) {
      return null;
    }
    const formatted = new Date(lastSyncedAt).toLocaleString();
    return `${tr("Last synced")}: ${formatted}`;
  })();

  return (
    <div className={styles.toolBar}>
      {currentVendor ? (
        <div style={{ marginLeft: "0px", height: "35px" }}>
          <span
            className={globalVendorsStyles.readonlyMode}
            style={{
              marginLeft: "15px",
              fontSize: "20px",
              fontWeight: "bold",
              display: "inline-block",
              maxWidth: "100%",
            }}
          >
            {currentVendor?.vendor_name}
          </span>
        </div>
      ) : (
        <div>
          <div className={styles.title}></div>
        </div>
      )}
      <div className={styles.toolBarButtons}>
        {currentVendor && (
          <>
            {isHeritageVendor && catalogLastSyncedLabel && (
              <span
                className={styles.catalogSyncStatus}
                title={tr("Last branch catalog download for your company")}
              >
                {catalogLastSyncedLabel}
              </span>
            )}

            {currentVendor?.is_siteone &&
              (currentVendor?.is_siteone_verified === true ? (
                <span className="inline-block px-3 py-1 text-sm font-semibold text-green-800 bg-green-100 rounded-full m-2">
                  SiteOne Verified
                </span>
              ) : (
                <Tooltip title="Verify vendor by clicking the Update SiteOne Item Prices button">
                  <span className="inline-block px-3 py-1 text-sm font-semibold text-yellow-700 bg-yellow-200 rounded-full m-2">
                    Not SiteOne Verified
                  </span>
                </Tooltip>
              ))}

            {heritageStatus &&
              (heritageBadgeOpensCredentials ? (
                <Tooltip title={heritageBadgeTooltip}>
                  <button
                    type="button"
                    onClick={handleHeritageBadgeClick}
                    className={`inline-block px-3 py-1 text-sm font-semibold rounded-full m-2 cursor-pointer hover:brightness-95 touch-manipulation min-h-[44px] ${heritageBadgeClass}`}
                  >
                    {heritageBadgeLabel}
                  </button>
                </Tooltip>
              ) : heritageBadgeTooltip ? (
                <Tooltip title={heritageBadgeTooltip}>
                  <span
                    className={`inline-block px-3 py-1 text-sm font-semibold rounded-full m-2 ${heritageBadgeClass}`}
                  >
                    {heritageBadgeLabel}
                  </span>
                </Tooltip>
              ) : (
                <span
                  className={`inline-block px-3 py-1 text-sm font-semibold rounded-full m-2 ${heritageBadgeClass}`}
                >
                  {heritageBadgeLabel}
                </span>
              ))}
          </>
        )}

        <Button primary onClick={() => setIsCreateModalOpen(true)}>
          {tr("New Vendor")}
        </Button>

        <VendorFormModal
          mode="create"
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          refetch={refetch}
        />
      </div>
    </div>
  );
};

export default VendorsToolBar;
